import type { GraphNode, GraphVisualizationFrame, SaleskoOAuthProvider } from "@salesko/contracts";
import { AgentAvatar, Button } from "@salesko/design-system";
import {
  AlertCircle,
  Apple,
  AtSign,
  Bot,
  CheckCircle2,
  ChevronRight,
  Linkedin,
  Loader2,
  Minus,
  Send,
  Sparkles,
  UserRound,
  type LucideIcon
} from "lucide-react";
import type { ChangeEvent, FormEvent, KeyboardEvent, RefObject, SyntheticEvent } from "react";
import { GoogleGlyph } from "../../components/GoogleGlyph";
import { useI18n, type Dictionary } from "../../i18n";
import type { StoredSaleskoSession } from "../../lib/api";
import { entityMeta } from "../entityMeta";
import { detailSubtitle, initialsOf } from "../lib/detail";
import type { AgentChatMessage, AgentChatMode, AgentChatResult, MentionQuery } from "../types";

const authProviderMeta = {
  google: { icon: UserRound, label: "Google" },
  apple: { icon: Apple, label: "Apple" },
  linkedin: { icon: Linkedin, label: "LinkedIn" }
} satisfies Record<SaleskoOAuthProvider, { icon: LucideIcon; label: string }>;

/**
 * Handing a selected entity to the Private Agent. The Generic Agent runs
 * synchronously on Salesko and has no research runtime of its own, so the only
 * honest action here is to pass the entity to the owner's own agent.
 */
export type AgentOverlayHandoff = {
  agentName: string;
  onHandoff: () => void;
};

type AgentOverlayProps = {
  activeMentionIndex: number;
  agentAuthLabel: string;
  agentOpen: boolean;
  agentThreadRef: RefObject<HTMLDivElement | null>;
  availableAuthProviders: SaleskoOAuthProvider[];
  chatMessages: AgentChatMessage[];
  frame: GraphVisualizationFrame;
  /** Present only when a person/opportunity is selected and a Private Agent exists. */
  handoff: AgentOverlayHandoff | null;
  isGenerating: boolean;
  mentionCandidates: GraphNode[];
  mentionQuery: MentionQuery | null;
  message: string;
  mode: AgentChatMode;
  mutationError: string | null;
  onAuthProviderSignIn: (provider: SaleskoOAuthProvider) => void;
  onClose: () => void;
  onMentionSelect: (node: GraphNode) => void;
  onMessageChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onMessageCursorChange: (event: SyntheticEvent<HTMLTextAreaElement>) => void;
  onMessageKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onMessageKeyUp: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onModeChange: (mode: AgentChatMode) => void;
  onOpen: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onUseSample: () => void;
  readOnly: boolean;
  sampleText: string;
  storedSession: StoredSaleskoSession | null;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
};

export function AgentOverlay({
  activeMentionIndex,
  agentAuthLabel,
  agentOpen,
  agentThreadRef,
  availableAuthProviders,
  chatMessages,
  frame,
  handoff,
  isGenerating,
  mentionCandidates,
  mentionQuery,
  message,
  mode,
  mutationError,
  onAuthProviderSignIn,
  onClose,
  onMentionSelect,
  onMessageChange,
  onMessageCursorChange,
  onMessageKeyDown,
  onMessageKeyUp,
  onModeChange,
  onOpen,
  onSubmit,
  onUseSample,
  readOnly,
  sampleText,
  storedSession,
  textareaRef
}: AgentOverlayProps) {
  const { t } = useI18n();
  if (readOnly) {
    return null;
  }

  const mentionListId = "agent-mention-list";
  const activeMention = mentionCandidates[Math.min(activeMentionIndex, mentionCandidates.length - 1)] ?? null;
  const activeMentionOptionId = mentionQuery && activeMention ? mentionOptionId(activeMention.id) : undefined;

  return (
    <>
      {!agentOpen ? (
        <button
          type="button"
          className="agent-fab"
          onClick={onOpen}
          aria-label={t.agentChat.openAgentInputAriaLabel}
          aria-expanded={false}
        >
          <span className="agent-fab-spark" aria-hidden="true">
            <Sparkles size={16} />
          </span>
          <span>{t.agentChat.graphChat}</span>
        </button>
      ) : null}

      <div
        className={agentOpen ? "agent-overlay is-open" : "agent-overlay"}
        role="dialog"
        aria-label={t.agentChat.agentInputAriaLabel}
        aria-hidden={!agentOpen}
      >
        <div className="agent-overlay-head">
          <div className="agent-overlay-title">
            <p className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <AgentAvatar size="xs" variant="generic" />
              <span>{t.agentChat.genericAgentName}</span>
              <span>{t.agentChat.genericAgentSubtitle}</span>
            </p>
            <strong>{t.agentChat.headline}</strong>
            <p className="agent-overlay-subtitle">{t.agentChat.subtitle}</p>
          </div>
          <div className="agent-overlay-actions">
            {handoff ? (
              <Button
                iconLeft={<AgentAvatar size="xs" variant="private" />}
                onClick={handoff.onHandoff}
                size="sm"
                variant="secondary"
              >
                {t.agentChat.handoff(handoff.agentName)}
              </Button>
            ) : null}
            {message.trim().length === 0 ? (
              <button
                type="button"
                className="agent-sample-chip agent-sample-chip-header"
                onClick={onUseSample}
                title={sampleText}
                aria-label={t.agentChat.useExample}
              >
                <Sparkles size={14} aria-hidden="true" />
                <span className="agent-sample-chip-tag">{t.agentChat.exampleOnly}</span>
                <span className="agent-sample-chip-text">{sampleText}</span>
              </button>
            ) : null}
            {storedSession ? (
              <button type="button" className="secondary-button agent-auth-button" title={t.agentChat.signedInTitle}>
                <UserRound size={16} />
                <span>{agentAuthLabel}</span>
              </button>
            ) : (
              <div className="agent-auth-providers" aria-label={t.agentChat.signInOptionsAriaLabel}>
                {availableAuthProviders.map((provider) => {
                  const providerMeta = authProviderMeta[provider];
                  const ProviderIcon = providerMeta.icon;
                  return (
                    <button
                      type="button"
                      key={provider}
                      className="secondary-button agent-auth-button"
                      onClick={() => onAuthProviderSignIn(provider)}
                      title={t.common.signInWith(providerMeta.label)}
                    >
                      {provider === "google" ? <GoogleGlyph size={16} /> : <ProviderIcon size={16} />}
                      <span>{providerMeta.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label={t.agentChat.collapseAgentInputAriaLabel}
              title={t.agentChat.collapseTitle}
            >
              <Minus size={18} />
            </button>
          </div>
        </div>
        <div className="agent-mode-toggle" role="group" aria-label={t.agentChat.modeToggleAriaLabel}>
          <button
            type="button"
            className={mode === "graph-copilot" ? "agent-mode-toggle-option is-active" : "agent-mode-toggle-option"}
            aria-pressed={mode === "graph-copilot"}
            onClick={() => onModeChange("graph-copilot")}
            title={t.agentChat.modeWriterHint}
          >
            <span>{t.agentChat.modeWriterLabel}</span>
          </button>
          <button
            type="button"
            className={mode === "seeker" ? "agent-mode-toggle-option is-active" : "agent-mode-toggle-option"}
            aria-pressed={mode === "seeker"}
            onClick={() => onModeChange("seeker")}
            title={t.agentChat.modeSeekerHint}
          >
            <span>{t.agentChat.modeSeekerLabel}</span>
          </button>
        </div>
        <div
          className="agent-thread"
          ref={agentThreadRef}
          role="log"
          aria-live="polite"
          aria-label={t.agentChat.agentConversationAriaLabel}
        >
          {chatMessages.map((chatMessage) => (
            <AgentChatBubble chatMessage={chatMessage} key={chatMessage.id} />
          ))}
        </div>
        <form className="agent-prompt" onSubmit={onSubmit}>
          <label htmlFor="agent-message">{t.agentChat.messageLabel}</label>
          <div className="agent-input-shell">
            <textarea
              aria-activedescendant={activeMentionOptionId}
              aria-autocomplete="list"
              aria-controls={mentionQuery ? mentionListId : undefined}
              aria-expanded={Boolean(mentionQuery)}
              id="agent-message"
              onChange={onMessageChange}
              onClick={onMessageCursorChange}
              onKeyDown={onMessageKeyDown}
              onKeyUp={onMessageKeyUp}
              placeholder={mode === "seeker" ? t.agentChat.seekerMessagePlaceholder : t.agentChat.messagePlaceholder}
              ref={textareaRef}
              role="combobox"
              value={message}
            />
            {mentionQuery ? (
              <div
                className="mention-picker"
                id={mentionListId}
                role="listbox"
                aria-label={t.agentChat.mentionListAriaLabel}
              >
                <div className="mention-picker-head">
                  <AtSign size={15} />
                  <span>
                    {mentionQuery.text ? t.agentChat.mentionWithText(mentionQuery.text) : t.agentChat.mentionCurrentEntity}
                  </span>
                </div>
                {mentionCandidates.length > 0 ? (
                  mentionCandidates.map((node, index) => {
                    const meta = entityMeta[node.type];
                    const Icon = meta.icon;
                    const active = index === activeMentionIndex;
                    return (
                      <button
                        aria-selected={active}
                        className={active ? "mention-option is-active" : "mention-option"}
                        id={mentionOptionId(node.id)}
                        key={node.id}
                        onClick={() => onMentionSelect(node)}
                        onMouseDown={(event) => event.preventDefault()}
                        role="option"
                        type="button"
                      >
                        <span className={`mini-avatar mini-avatar-${meta.tone}`} aria-hidden="true">
                          {node.type === "person" ? (
                            <span className="mini-avatar-initials">{initialsOf(node.label)}</span>
                          ) : (
                            <Icon size={15} />
                          )}
                        </span>
                        <span>
                          <strong>{node.label}</strong>
                          <small>{detailSubtitle(node, frame, t)}</small>
                        </span>
                        <ChevronRight size={16} />
                      </button>
                    );
                  })
                ) : (
                  <p className="mention-empty">{t.agentChat.noMentionMatches}</p>
                )}
              </div>
            ) : null}
            <div className="agent-composer-actions">
              <span className="agent-composer-hint">
                <AtSign size={14} />
                <span>{t.agentChat.composerHint}</span>
              </span>
              <button
                aria-label={isGenerating ? t.agentChat.generating : t.agentChat.send}
                className="agent-send-button"
                disabled={isGenerating || message.trim().length === 0}
                title={isGenerating ? t.agentChat.generating : t.agentChat.send}
                type="submit"
              >
                {isGenerating ? <Loader2 className="spin" size={17} /> : <Send size={17} />}
              </button>
            </div>
          </div>
          {mutationError ? (
            <p className="error-line">
              <AlertCircle size={16} />
              <span>{mutationError}</span>
            </p>
          ) : null}
        </form>
      </div>
    </>
  );
}

function mentionOptionId(nodeId: string) {
  return `agent-mention-option-${nodeId}`;
}

function AgentChatBubble({ chatMessage }: { chatMessage: AgentChatMessage }) {
  const { t } = useI18n();
  const result = chatMessage.result;
  const isAssistant = chatMessage.role === "assistant";
  const paragraphs = messageParagraphs(chatMessage.content);
  return (
    <article
      className={`agent-message agent-message-${chatMessage.role}${chatMessage.status === "pending" ? " is-pending" : ""}${chatMessage.status === "error" ? " is-error" : ""}`}
    >
      <span className={isAssistant ? "agent-message-avatar agent-message-avatar-assistant" : "agent-message-avatar"} aria-hidden="true">
        {isAssistant ? <Bot size={16} /> : <UserRound size={16} />}
      </span>
      <div className="agent-message-body">
        <div className="agent-message-copy">
          {paragraphs.map((paragraph, index) => (
            <p key={`${index}:${paragraph}`}>{paragraph}</p>
          ))}
        </div>
        {chatMessage.status === "pending" ? (
          <span className="agent-status-line">
            <Loader2 className="spin" size={14} />
            {t.agentChat.structuringGraphFrame}
          </span>
        ) : null}
        {chatMessage.status === "error" ? (
          <span className="agent-status-line agent-status-error">
            <AlertCircle size={14} />
            {t.agentChat.requestFailed}
          </span>
        ) : null}
        {/* Seeker turns never carry a frame/commit (read-only discussion) — the receipt
            card only makes sense for the graph-copilot extraction result. */}
        {result?.mode === "graph-copilot" ? <AgentResultSummary result={result} /> : null}
      </div>
    </article>
  );
}

function AgentResultSummary({ result }: { result: Extract<AgentChatResult, { mode: "graph-copilot" }> }) {
  const { t } = useI18n();
  const needsReview = result.commit.status === "needs_review";
  return (
    <div className={needsReview ? "agent-result-card needs-review" : "agent-result-card"}>
      <div className="agent-result-head">
        <span className="agent-result-state">
          <CheckCircle2 size={16} />
          {needsReview ? t.agentChat.needsReview : t.agentChat.executed}
        </span>
        {result.source ? <span className="agent-source-pill">{sourceLabel(result.source, t)}</span> : null}
        <span className="agent-source-pill">{t.graphStage.relationshipGraphLabel}</span>
      </div>
      <dl className="agent-result-metrics">
        <div>
          <dt>{t.agentChat.nodesLabel}</dt>
          <dd>{result.frame.graph.nodes.length}</dd>
        </div>
        <div>
          <dt>{t.agentChat.edgesLabel}</dt>
          <dd>{result.frame.graph.edges.length}</dd>
        </div>
        <div>
          <dt>{t.agentChat.peopleLabel}</dt>
          <dd>{result.frame.ui.stats.people}</dd>
        </div>
        <div>
          <dt>{t.agentChat.eventsLabel}</dt>
          <dd>{result.frame.ui.stats.events}</dd>
        </div>
      </dl>
    </div>
  );
}

function messageParagraphs(content: string) {
  return content
    .split(/ (?=Execution:|I still need)/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function sourceLabel(source: AgentChatResult["source"], t: Dictionary) {
  switch (source) {
    case "tanstack-ai-generate":
      return t.agentChat.sourceModel;
    case "deterministic-rule":
      return t.agentChat.sourceRules;
    default:
      return t.agentChat.sourceAgent;
  }
}
